import {
  type EmailSignaturesActions,
  initialEmailSignatureContextValues,
} from '@reducers/email-signature-reducer';
import { useCallback } from 'react';

import { EmailSignatureFieldTypes } from '../../../consts';
// types
import type { EmailSignatureSocialLinkId, EmailSignatureV2 } from '../../../types';
// utils
import type EmailSignatureImagesUploader from '../../EmailSignatureImagesUploader';
import useInitialEmailSignature from '../../useInitialEmailSignature';
// hooks and context
import useSocialLinksIconsGenerator from '../../useSocialLinksIconsGenerator';
import { useSocialIconsGeneratorSwitch } from '../useIconGeneratorSwitches';

type SocialServicesProps = {
  emailSignature: EmailSignatureV2;
  emailSignatureDispatch: (action: EmailSignaturesActions) => void;
  emailSignatureHasBeenLoaded: boolean;
  handleOnErrorUploading: (fieldType: EmailSignatureFieldTypes) => void;
  uploadEmailSignatureFieldImagesThunk: (
    uploadImage: (emailSignatureImagesUploader: EmailSignatureImagesUploader) => void,
  ) => void;
};

/*
    This component is a context wrapper that provides all the core services for the email signature side editor,
    it should be used with the email signature Side Editor component. This component allows SideEditor component to
    be reused in different editors (currently used in EmailSignatureMainEditor and EmailSignatureOnboardingEditor)
*/

function useSocialServices({
  emailSignature,
  emailSignatureDispatch,
  emailSignatureHasBeenLoaded,
  handleOnErrorUploading,
  uploadEmailSignatureFieldImagesThunk,
}: SocialServicesProps): void {
  const { initialEmailSignature, initialEmailSignatureHasBeenLoaded } = useInitialEmailSignature(
    emailSignature,
    emailSignatureHasBeenLoaded,
  );
  const turnOnSocialLinksIconsGenerator = useSocialIconsGeneratorSwitch(
    emailSignatureHasBeenLoaded,
    initialEmailSignatureHasBeenLoaded,
    initialEmailSignature || initialEmailSignatureContextValues,
    emailSignature,
  );

  const handleUpdateNewLocalSocialIconUrl = useCallback(
    (socialLinkId: EmailSignatureSocialLinkId, localImageUrl: string) => {
      emailSignatureDispatch({
        type: 'UPDATE_SOCIAL_LINK_ICON_URL',
        value: {
          fieldId: socialLinkId,
          updatedFieldValue: localImageUrl,
        },
      });

      uploadEmailSignatureFieldImagesThunk((emailSignatureImagesUploader) => {
        emailSignatureImagesUploader.uploadImage(
          socialLinkId,
          localImageUrl,
          EmailSignatureFieldTypes.SOCIAL,
          (fieldType: EmailSignatureFieldTypes) => {
            handleOnErrorUploading(fieldType);
            emailSignatureDispatch({
              type: 'DELETE_SOCIAL_LINK',
              value: socialLinkId,
            });
          },
        );
      });
    },
    [emailSignatureDispatch, uploadEmailSignatureFieldImagesThunk, handleOnErrorUploading],
  );

  useSocialLinksIconsGenerator(
    emailSignature,
    handleUpdateNewLocalSocialIconUrl,
    turnOnSocialLinksIconsGenerator,
  );
}

export { useSocialServices };
